import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth, useTheme, useLanguage, API } from '@/App';
import { getTranslation } from '@/translations';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import {
  ArrowLeft,
  BarChart3,
  Calendar,
  TrendingUp,
  Bot,
  Activity
} from 'lucide-react';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  Filler
} from 'chart.js';
import { Line } from 'react-chartjs-2';

// Register ChartJS components
ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  Filler
);

const OverallAnalyticsPage = () => {
  const { user, token } = useAuth();
  const { theme } = useTheme();
  const { language } = useLanguage();
  const navigate = useNavigate();
  const t = (key) => getTranslation(language, key);

  const [loading, setLoading] = useState(true);
  const [period, setPeriod] = useState('day');
  const [analyticsData, setAnalyticsData] = useState({
    aiAgents: [],
    chart_data: [],
    period: 'day'
  });

  const headers = token ? { Authorization: `Bearer ${token}` } : {};

  useEffect(() => {
    fetchAnalytics();
  }, [period]);

  const fetchAnalytics = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/analytics/overall?period=${period}`, {
        headers,
        withCredentials: true
      });
      setAnalyticsData(response.data);
    } catch (error) {
      toast.error(language === 'id' ? 'Gagal memuat data analytics' : 'Failed to load analytics data');
      console.error('Analytics fetch error:', error);
    } finally {
      setLoading(false);
    }
  };

  const getPeriodLabel = () => {
    if (period === 'day') return language === 'id' ? 'Harian' : 'Daily';
    if (period === 'month') return language === 'id' ? 'Bulanan' : 'Monthly';
    if (period === 'year') return language === 'id' ? 'Tahunan' : 'Yearly';
    return period;
  };

  const totalChats = analyticsData.aiAgents.reduce((sum, bot) => sum + bot.total_chats, 0);

  // Chart.js configuration
  const isDark = theme === 'dark';
  const textColor = isDark ? '#e2e8f0' : '#1e293b';
  const gridColor = isDark ? '#334155' : '#e2e8f0';

  // Colors for each aiAgent line
  const colors = ['#10B981', '#3B82F6', '#8B5CF6', '#EF4444', '#F59E0B', '#EC4899', '#06B6D4'];

  // Build datasets for each aiAgent
  const datasets = analyticsData.aiAgents.map((aiAgent, index) => ({
    label: aiAgent.name,
    data: analyticsData.chart_data.map(d => d[aiAgent.name] || 0),
    borderColor: aiAgent.color || colors[index % colors.length],
    backgroundColor: `${aiAgent.color || colors[index % colors.length]}20`,
    tension: 0.4,
    fill: false,
    pointRadius: 3,
    pointHoverRadius: 5
  }));

  const chartData = {
    labels: analyticsData.chart_data.map(d => d.period),
    datasets: datasets
  };

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    interaction: {
      mode: 'index',
      intersect: false,
    },
    plugins: {
      legend: {
        display: true,
        position: 'bottom',
        labels: {
          color: textColor,
          padding: 15,
          usePointStyle: true,
          font: { size: 12 }
        }
      },
      tooltip: {
        backgroundColor: isDark ? '#1e293b' : '#ffffff',
        titleColor: textColor,
        bodyColor: textColor,
        borderColor: gridColor,
        borderWidth: 1
      }
    },
    scales: {
      x: {
        grid: { color: gridColor },
        ticks: { color: textColor }
      },
      y: {
        grid: { color: gridColor },
        ticks: { color: textColor, precision: 0 },
        beginAtZero: true
      }
    }
  };

  return (
    <div className="min-h-screen bg-slate-50 dark:bg-[#0A0A0A]">
      {/* Header */}
      <header className="sticky top-0 backdrop-blur-xl bg-white/85 dark:bg-[#0A0A0A]/85 border-b border-slate-200 dark:border-zinc-800/80 z-20">
        <div className="max-w-7xl mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            {/* <Link to="/dashboard">
              <Button variant="ghost" size="sm" className="dark:text-zinc-200 dark:hover:bg-zinc-800">
                <ArrowLeft className="w-5 h-5" />
              </Button>
            </Link> */}
            <Link to="/" className="flex items-center gap-2">
              <img src="/images/logo.png" alt="Nerava" className="w-8 h-8" />
              <span className="font-bold text-slate-900 dark:text-zinc-50">Nerava</span>
            </Link>
          </div>
          <div className="flex items-center gap-2">
            <BarChart3 className="w-5 h-5 text-indigo-600 dark:text-indigo-400" />
            <h1 className="text-lg sm:text-2xl font-semibold tracking-tight text-slate-900 dark:text-zinc-50" style={{ fontFamily: 'Manrope' }}>
              {language === 'id' ? 'Analytics Keseluruhan' : 'Overall Analytics'}
            </h1>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 py-8">
        {loading ? (
          <div className="flex items-center justify-center py-20">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600"></div>
          </div>
        ) : (
          <>
            {/* Summary Cards */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
              <div className="bg-white dark:bg-zinc-900 rounded-xl border border-slate-200 dark:border-zinc-800 p-6">
                <div className="flex items-center gap-3 mb-2">
                  <div className="w-10 h-10 rounded-xl bg-indigo-100 dark:bg-indigo-950/30 flex items-center justify-center">
                    <Bot className="w-5 h-5 text-indigo-600 dark:text-indigo-400" />
                  </div>
                  <span className="text-sm text-slate-600 dark:text-zinc-400">
                    {language === 'id' ? 'Total AI Agent' : 'Total AI Agents'}
                  </span>
                </div>
                <p className="text-3xl font-bold text-slate-900 dark:text-zinc-50">
                  {analyticsData.aiAgents.length}
                </p>
              </div>

              <div className="bg-white dark:bg-zinc-900 rounded-xl border border-slate-200 dark:border-zinc-800 p-6">
                <div className="flex items-center gap-3 mb-2">
                  <div className="w-10 h-10 rounded-xl bg-indigo-100 dark:bg-indigo-950/30 flex items-center justify-center">
                    <Activity className="w-5 h-5 text-indigo-600 dark:text-indigo-400" />
                  </div>
                  <span className="text-sm text-slate-600 dark:text-zinc-400">
                    {language === 'id' ? 'Total Percakapan' : 'Total Conversations'}
                  </span>
                </div>
                <p className="text-3xl font-bold text-slate-900 dark:text-zinc-50">
                  {totalChats.toLocaleString()}
                </p>
              </div>

              <div className="bg-white dark:bg-zinc-900 rounded-xl border border-slate-200 dark:border-zinc-800 p-6">
                <div className="flex items-center gap-3 mb-2">
                  <div className="w-10 h-10 rounded-xl bg-purple-100 dark:bg-purple-900/20 flex items-center justify-center">
                    <TrendingUp className="w-5 h-5 text-purple-600 dark:text-purple-400" />
                  </div>
                  <span className="text-sm text-slate-600 dark:text-zinc-400">
                    {language === 'id' ? 'Rata-rata per Bot' : 'Avg per Bot'}
                  </span>
                </div>
                <p className="text-3xl font-bold text-slate-900 dark:text-zinc-50">
                  {analyticsData.aiAgents.length > 0 
                    ? Math.round(totalChats / analyticsData.aiAgents.length).toLocaleString()
                    : 0
                  }
                </p>
              </div>
            </div>

            {/* Chart Section */}
            <div className="bg-white dark:bg-zinc-900 rounded-xl border border-slate-200 dark:border-zinc-800 p-6 mb-8">
              <div className="flex items-center justify-between mb-6">
                <div className="flex items-center gap-2">
                  <Calendar className="w-5 h-5 text-indigo-600 dark:text-indigo-400" />
                  <h2 className="text-base sm:text-lg font-semibold text-slate-900 dark:text-zinc-50">
                    {language === 'id' ? 'Grafik Percakapan' : 'Conversation Chart'}
                  </h2>
                </div>
                <Select value={period} onValueChange={setPeriod}>
                  <SelectTrigger className="w-40 dark:bg-zinc-800 dark:text-zinc-50 dark:border-zinc-700">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="dark:bg-zinc-900 dark:border-zinc-800">
                    <SelectItem value="day" className="dark:text-zinc-50 dark:focus:bg-slate-700">
                      {language === 'id' ? 'Harian' : 'Daily'}
                    </SelectItem>
                    <SelectItem value="month" className="dark:text-zinc-50 dark:focus:bg-slate-700">
                      {language === 'id' ? 'Bulanan' : 'Monthly'}
                    </SelectItem>
                    <SelectItem value="year" className="dark:text-zinc-50 dark:focus:bg-slate-700">
                      {language === 'id' ? 'Tahunan' : 'Yearly'}
                    </SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {analyticsData.chart_data.length === 0 ? (
                <div className="text-center py-12 text-slate-500 dark:text-zinc-400">
                  <BarChart3 className="w-12 h-12 mx-auto mb-3 opacity-50" />
                  <p>{language === 'id' ? 'Belum ada data percakapan' : 'No conversation data yet'}</p>
                </div>
              ) : (
                <div style={{ height: '400px' }}>
                  <Line data={chartData} options={chartOptions} />
                </div>
              )}
            </div>

            {/* AI Agent List */}
            <div className="bg-white dark:bg-zinc-900 rounded-xl border border-slate-200 dark:border-zinc-800 p-6">
              <h2 className="text-base sm:text-lg font-semibold text-slate-900 dark:text-zinc-50 mb-4">
                {language === 'id' ? 'Detail per AI Agent' : 'AI Agent Details'}
              </h2>
              <div className="space-y-3">
                {analyticsData.aiAgents.map((aiAgent, index) => (
                  <div
                    key={aiAgent.id}
                    className="flex items-center justify-between p-4 bg-slate-50 dark:bg-[#0A0A0A] rounded-xl hover:bg-slate-100 dark:hover:bg-zinc-800 transition-colors"
                  >
                    <div className="flex items-center gap-3">
                      <div
                        className="w-10 h-10 rounded-xl flex items-center justify-center"
                        style={{ backgroundColor: aiAgent.color + '20' }}
                      >
                        <Bot className="w-5 h-5" style={{ color: aiAgent.color }} />
                      </div>
                      <div>
                        <p className="font-medium text-slate-900 dark:text-zinc-50">{aiAgent.name}</p>
                        <p className="text-sm text-slate-500 dark:text-zinc-400">
                          {aiAgent.total_chats} {language === 'id' ? 'percakapan' : 'conversations'}
                        </p>
                      </div>
                    </div>
                    <Link to={`/analytics/${aiAgent.id}`}>
                      <Button variant="outline" size="sm" className="dark:border-zinc-700 dark:text-zinc-200 dark:hover:bg-zinc-800">
                        {language === 'id' ? 'Lihat Detail' : 'View Details'}
                      </Button>
                    </Link>
                  </div>
                ))}
              </div>
            </div>
          </>
        )}
      </main>
    </div>
  );
};

export default OverallAnalyticsPage;
